"use client";

import { useReducedMotion } from "framer-motion";
import type * as React from "react";
import { useEffect, useId, useRef } from "react";

import { cn } from "../lib/cn";

/**
 * AndoWordmark — the animated brand lockup.
 *
 * Pencil-line elephant (left, facing the word) + "Ando" in Space Grotesk
 * ("And" ink, "o" orange). On mount a clean stroke is *extruded from the
 * trunk*, flies over the word as a fixed-length segment, wraps into the
 * "o" and dissolves into the real font glyph — leaving a normal "Ando".
 * The elephant gazes at / leans toward the cursor and blinks. Reduced
 * motion → the static lockup (real "o" shown immediately).
 *
 * Space Grotesk is loaded app-wide via @fontsource-variable (`font-display`).
 */
const OUTLINE = "M334.339 71.8862C341.102 67.9975 348.036 60.146 355.233 55.4839C379.105 40.0185 407.401 27.9262 436.292 34.8598C459.359 40.6064 477.66 56.2226 489.803 76.2031C492.21 80.165 497.02 89.8886 497.879 94.3779C500.335 100.593 501.283 104.221 502.606 110.797C503.905 117.101 504.51 122.681 504.329 129.154C504.911 144.13 504.256 152.089 499.115 166.086C492.786 182.458 485.267 191.697 475.863 206.132C472.948 210.609 470.736 216.869 468.353 221.632C484.168 235.553 495.389 253.942 500.54 274.371C501.961 279.982 502.547 285.045 503.553 290.665C505.946 292.238 506.722 293.283 508.626 295.377C506.195 296.037 505.746 295.705 504.403 297.252L504.373 299.684C507.957 327.682 504.246 377.409 500.589 405.734C499.994 410.709 499.11 419.245 498.001 424.02C497.777 432.409 490.482 435.651 483.089 436.149C476.102 436.857 458.285 438.229 452.762 432.882C444.574 424.957 453.294 403.571 440.282 395.607C438.099 394.274 437.64 393.219 434.867 393.605L434.359 394.874C432.816 404.664 431.112 419.035 428.426 428.185C419.53 435.729 401.414 435.509 391.082 430.973C383.704 427.736 386.219 415.431 385.833 408.815C385.487 402.907 384.954 398.908 383.421 393.038C381.341 392.101 377.943 391.583 375.614 391.12C373.368 398.8 367.625 429.533 365.057 432.887C360.78 435.543 358.197 436.452 353.338 437.78C344.979 439.821 335.565 440.211 327.074 438.746C322.059 437.882 316.239 436.232 313.294 431.749C307.65 423.165 314.491 394.943 304.632 381.207C304.539 381.076 304.437 380.944 304.339 380.812C301.873 379.201 300.365 378.795 297.391 378.746C297.025 390.016 295.306 403.527 293.46 414.655C293.011 418.488 292.108 428.732 289.066 431.081C279.369 438.585 253.184 438.659 243.327 431.837C239.506 429.191 240.044 418.454 240.137 414C239.091 406.828 238.907 397.853 237.839 389.953C235.357 372.409 232.352 354.938 228.83 337.575C226.938 327.755 225.047 316.583 222.709 306.94L222.111 306.164C219.329 306.178 203.588 309.411 198.29 309.997C177.485 310.436 186.181 299.977 189.264 285.573C163.86 287.765 140.418 287.174 117.272 274.557C98.1132 266.476 79.2304 244.743 71.4164 225.856C65.1815 210.784 61.9804 189.327 61.3378 172.916C59.0878 157.903 61.4232 142.232 55.1566 127.533C53.6683 124.041 46.4838 114.803 49.8261 111.883C53.8827 108.34 63.2709 111.314 68.2357 110.868C72.5604 110.244 75.369 104.699 77.8217 101.523C79.5434 99.2949 84.3529 95.2802 86.703 98.5971C95.7221 111.329 97.1356 136.874 97.6488 152.014C98.9379 163.571 98.5765 168.386 101.679 180.288C103.504 184.596 104.993 187.969 107.179 192.095L111.125 197.584C113.631 199.846 118.131 202.674 119.216 203.701C121.017 204.31 123.053 205.056 124.855 205.58C128.274 206.021 130.267 206.099 133.718 206.154L139.642 205.156C140.354 205.022 140.359 204.91 141.027 204.519L141.364 203.171C140.282 195.65 138.957 186.863 136.512 179.764C133.386 170.686 128.131 160.327 126.479 150.882C121.017 133.516 123.635 112.686 129.432 95.7392C130.449 91.7514 133.11 86.2607 134.843 82.3632C138.012 75.3183 141.739 70.6294 146.762 64.8515C150.72 60.6064 153.903 57.7119 158.559 54.2724C175.393 43.0078 193.217 39.9526 212.417 47.3329C224.126 51.8339 230.878 57.457 240.501 65.183C270.822 57.8027 305.682 58.9458 334.339 71.8862Z";
const EAR = "M136.819 87.5351C147.323 74.7685 166.427 62.3818 183.667 65.3247C193.468 65.937 205.885 71.5356 212.666 78.6406C191.231 92.0293 177.284 107.718 168.64 131.84C166.719 138.247 164.589 145.311 163.531 151.874C160.613 169.965 159.588 192.346 142.511 203.7L141.603 203.525L141.365 203.171C140.283 195.65 138.957 186.863 136.513 179.764C133.387 170.686 128.131 160.327 126.48 150.882C121.018 133.516 123.636 112.686 129.433 95.7392C130.449 91.7514 133.111 86.2607 134.844 82.3632C135.336 84.8525 135.367 85.4677 136.819 87.5351Z";
const EYE_PATH = "M265.38 169.96C270.858 168.305 276.638 171.414 278.278 176.897C279.914 182.38 276.789 188.151 271.301 189.774C265.838 191.39 260.096 188.28 258.463 182.82C256.831 177.36 259.925 171.608 265.38 169.96Z";
const EYE = { x: 268.4, y: 179.8 };
const TRUNK = { x: 48, y: 116 };
const EVB = "32.5 13.9 490.1 440.3";
const ORANGE = "#E5651A";
const INK = "#16110D";

export interface AndoWordmarkProps {
  className?: string;
  /** Word font-size in px (drives the whole lockup scale). Default 64. */
  fontSize?: number;
  /** Play the draw-in animation on mount. Default true. */
  animate?: boolean;
  /** Elephant follows the cursor (gaze + lean) + blinks. Default true. */
  interactive?: boolean;
  /** "ink" (dark word, light bg) or "light" (light word, dark bg). Default "ink". */
  tone?: "ink" | "light";
  /** Soft warm halo behind the elephant ("Medium" glow). Default true. */
  glow?: boolean;
}

export const AndoWordmark: React.FC<AndoWordmarkProps> = ({
  className,
  fontSize = 64,
  animate = true,
  interactive = true,
  tone = "ink",
  glow = true,
}) => {
  const reduced = useReducedMotion() ?? false;
  const fid = useId().replace(/[:]/g, "");
  const lockRef = useRef<HTMLDivElement>(null);
  const eleRef = useRef<SVGSVGElement>(null);
  const connRef = useRef<SVGSVGElement>(null);
  const drawRef = useRef<SVGPathElement>(null);
  const oRealRef = useRef<HTMLSpanElement>(null);
  const trunkRef = useRef<SVGCircleElement>(null);
  const eyeRef = useRef<SVGCircleElement>(null);
  const leanRef = useRef<SVGGElement>(null);
  const pupilRef = useRef<SVGGElement>(null);
  const dispRef = useRef<SVGFEDisplacementMapElement>(null);

  useEffect(() => {
    const ele = eleRef.current;
    const conn = connRef.current;
    const draw = drawRef.current;
    const oReal = oRealRef.current;
    const lock = lockRef.current;
    const lean = leanRef.current;
    const pupil = pupilRef.current;
    const disp = dispRef.current;
    const trunkM = trunkRef.current;
    const eyeM = eyeRef.current;
    if (!ele || !conn || !draw || !oReal || !lock || !lean || !pupil || !trunkM || !eyeM) return;

    const MIR = -1;
    const live = interactive && !reduced;
    const raw = { x: 0, y: 0, seen: false };
    const onMove = (e: PointerEvent): void => {
      raw.x = e.clientX;
      raw.y = e.clientY;
      raw.seen = true;
    };
    if (live) window.addEventListener("pointermove", onMove, { passive: true });

    const ctr = (el: Element): { x: number; y: number } => {
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    };

    const buildPath = (): { T: number; seg: number } => {
      const c = lock.getBoundingClientRect();
      conn.setAttribute("viewBox", `0 0 ${c.width} ${c.height}`);
      const tip = ctr(trunkM);
      const orr = oReal.getBoundingClientRect();
      const S = { x: tip.x - c.left, y: tip.y - c.top };
      const ox = orr.left + orr.width / 2 - c.left + orr.width * 0.08;
      const oy = orr.top + orr.height * 0.57 - c.top;
      // Match the real font "o": centre-line radius of the glyph's ring, not
      // the outer edge — otherwise the drawn circle reads bigger/looser than
      // the letter it becomes.
      const r = orr.height * 0.16;
      const E = { x: ox, y: oy - r };
      const apex = Math.min(S.y, E.y) - r * 2 * 1.15;
      const c1 = { x: S.x + (E.x - S.x) * 0.3, y: apex };
      const c2 = { x: E.x - (E.x - S.x) * 0.12, y: apex + r * 0.2 };
      let d = `M ${S.x.toFixed(1)} ${S.y.toFixed(1)} C ${c1.x.toFixed(1)} ${c1.y.toFixed(1)}, ${c2.x.toFixed(1)} ${c2.y.toFixed(1)}, ${E.x.toFixed(1)} ${E.y.toFixed(1)}`;
      const k = 0.5522847498;
      const quarter = Math.PI / 2;
      const a0 = -Math.PI / 2;
      const P = (a: number): [number, number] => [ox + r * Math.cos(a), oy + r * Math.sin(a)];
      const Tn = (a: number): [number, number] => [-Math.sin(a) * r * k, Math.cos(a) * r * k];
      for (let i = 0; i < 4; i++) {
        const a = a0 + quarter * i;
        const b = a + quarter;
        const p1 = P(a);
        const p2 = P(b);
        const t1 = Tn(a);
        const t2 = Tn(b);
        d += ` C ${(p1[0] + t1[0]).toFixed(1)} ${(p1[1] + t1[1]).toFixed(1)}, ${(p2[0] - t2[0]).toFixed(1)} ${(p2[1] - t2[1]).toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
      }
      draw.setAttribute("d", d);
      draw.setAttribute("stroke-width", (fontSize * 0.115).toFixed(1));
      const T = draw.getTotalLength();
      const circleLen = 2 * Math.PI * r;
      return { T, seg: Math.min(circleLen, T) };
    };

    const A = { active: false, started: false, t0: 0, T: 0, seg: 0, faded: false };
    const play = (): void => {
      oReal.style.opacity = "0";
      draw.style.opacity = "1";
      A.faded = false;
      const g = buildPath();
      A.T = g.T;
      A.seg = g.seg;
      A.started = false;
      A.active = true;
      draw.setAttribute("stroke-dasharray", `0 0 0 ${(A.T + 40).toFixed(1)}`);
    };

    const easeInOut = (p: number): number =>
      p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2;
    const dur = 1600;

    if (reduced || !animate) {
      oReal.style.opacity = "1";
      draw.style.opacity = "0";
    } else {
      play();
    }

    const gz = { x: 0, y: 0 };
    const ln = { x: 0, y: 0 };
    let bs = -9999;
    let nb = 2600;
    const blink = (t: number): number => {
      if (t > nb) {
        bs = t;
        nb = t + 2600 + 1800 * (0.5 + 0.5 * Math.sin(t * 0.013));
      }
      const p = (t - bs) / 150;
      return p >= 0 && p <= 1 ? 1 - Math.sin(p * Math.PI) * 0.9 : 1;
    };

    let rafId = 0;
    const loop = (t: number): void => {
      if (A.active) {
        if (!A.started) {
          A.t0 = t;
          A.started = true;
        }
        let p = (t - A.t0) / dur;
        if (p > 1) p = 1;
        const e = easeInOut(p);
        const head = e * A.T;
        const tail = Math.max(0, head - A.seg);
        draw.setAttribute(
          "stroke-dasharray",
          `0 ${tail.toFixed(1)} ${(head - tail).toFixed(1)} ${(A.T + 40).toFixed(1)}`,
        );
        if (p >= 0.94 && !A.faded) {
          A.faded = true;
          oReal.style.opacity = "1";
          draw.style.opacity = "0";
        }
        if (p >= 1) A.active = false;
      }
      const r = ele.getBoundingClientRect();
      let lx = 0;
      let ly = 0;
      if (live && raw.seen) {
        const dx = raw.x - (r.left + r.width / 2);
        const dy = raw.y - (r.top + r.height / 2);
        const dd = Math.hypot(dx, dy) || 1;
        const R = r.width * 2.4;
        let m = Math.max(0, 1 - dd / R);
        m = m * m * (3 - 2 * m);
        lx = (dx / dd) * m;
        ly = (dy / dd) * m;
      }
      ln.x += (lx - ln.x) * 0.06;
      ln.y += (ly - ln.y) * 0.06;
      lean.setAttribute("transform", `translate(${(MIR * ln.x * 13).toFixed(1)} ${(ln.y * 13).toFixed(1)})`);
      if (disp) disp.setAttribute("scale", (1.6 + Math.sin(t * 0.0015) * 0.7).toFixed(2));
      let gx = 0;
      let gy = 0;
      if (live && raw.seen) {
        const em = ctr(eyeM);
        const dx = raw.x - em.x;
        const dy = raw.y - em.y;
        const dd = Math.hypot(dx, dy) || 1;
        gx = ((MIR * dx) / dd) * 4.5;
        gy = (dy / dd) * 4.5;
      }
      gz.x += (gx - gz.x) * 0.15;
      gz.y += (gy - gz.y) * 0.15;
      const b = blink(t);
      pupil.setAttribute(
        "transform",
        `translate(${gz.x.toFixed(2)} ${gz.y.toFixed(2)}) translate(${EYE.x} ${EYE.y}) scale(1 ${b.toFixed(3)}) translate(${-EYE.x} ${-EYE.y})`,
      );
      // Keep animating only while drawing or when interactive; otherwise the
      // lockup settles (no perpetual rAF for static placements like the navbar).
      if (A.active || live) rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      if (live) window.removeEventListener("pointermove", onMove);
    };
  }, [reduced, interactive, animate, fontSize]);

  const eleH = fontSize * 1.27;
  // "Medium" warm halo — sized + blurred proportionally to the mark so it
  // reads the same at any scale.
  const haloSize = eleH * 1.12;
  const haloBlur = eleH * 0.13;
  return (
    <div
      ref={lockRef}
      aria-label="Ando"
      role="img"
      className={cn("relative inline-flex items-center", className)}
      style={{ gap: `${fontSize * 0.14}px` }}
    >
      <svg
        ref={connRef}
        aria-hidden="true"
        role="presentation"
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
      >
        <path
          ref={drawRef}
          fill="none"
          stroke={ORANGE}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transition: "opacity .24s ease" }}
        />
      </svg>
      <span style={{ position: "relative", display: "inline-grid", placeItems: "center" }}>
        {glow ? (
          <span
            aria-hidden
            style={{
              position: "absolute",
              width: `${haloSize}px`,
              height: `${haloSize}px`,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 50% 52%, rgba(255,180,100,0.5), rgba(255,122,26,0.18) 45%, transparent 70%)",
              filter: `blur(${haloBlur}px)`,
              pointerEvents: "none",
              zIndex: 0,
            }}
          />
        ) : null}
        <svg
          ref={eleRef}
          aria-hidden="true"
          role="presentation"
          viewBox={EVB}
          style={{
            position: "relative",
            zIndex: 1,
            height: `${eleH}px`,
            width: "auto",
            overflow: "visible",
            transform: "scaleX(-1)",
          }}
        >
        <defs>
          <filter id={`bf-${fid}`} x="-25%" y="-25%" width="150%" height="150%">
            <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves={2} seed={6} result="n" />
            <feDisplacementMap
              ref={dispRef}
              in="SourceGraphic"
              in2="n"
              scale="2"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
        <g ref={leanRef}>
          <g
            filter={`url(#bf-${fid})`}
            fill="none"
            stroke={ORANGE}
            strokeLinejoin="round"
            strokeLinecap="round"
          >
            <path d={OUTLINE} strokeWidth={2.4} vectorEffect="non-scaling-stroke" />
            <path d={EAR} strokeWidth={1.8} vectorEffect="non-scaling-stroke" />
            <path d={EYE_PATH} strokeWidth={1.8} vectorEffect="non-scaling-stroke" />
          </g>
          <g ref={pupilRef}>
            <circle cx={EYE.x} cy={EYE.y} r={6} fill={ORANGE} />
          </g>
          <circle ref={trunkRef} cx={TRUNK.x} cy={TRUNK.y} r={0.1} fill="none" />
          <circle ref={eyeRef} cx={EYE.x} cy={EYE.y} r={0.1} fill="none" />
        </g>
        </svg>
      </span>
      <span
        className="font-display"
        style={{
          fontWeight: 700,
          fontSize: `${fontSize}px`,
          letterSpacing: "-0.045em",
          lineHeight: 0.8,
          color: tone === "light" ? "#FBF7F1" : INK,
          whiteSpace: "nowrap",
        }}
      >
        And
        <span ref={oRealRef} style={{ color: ORANGE, opacity: 0, transition: "opacity .24s ease" }}>
          o
        </span>
      </span>
    </div>
  );
};
AndoWordmark.displayName = "AndoWordmark";
